import { useState } from "react";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Info,
  ShieldOff,
  CircleCheckBig,
  CircleOff,
  Eye,
  EyeOff,
  Package,
  BarChart3,
} from "lucide-react";
import { type ItemStatus } from "@/lib/api/item-statuses";
import { useItemStatuses } from "@/hooks/useItemStatuses";
import { cn } from "@/lib/utils";

const ItemStatusesSettingsPage = () => {
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: itemStatuses = [], isLoading: loading } = useItemStatuses({
    enabled: isTenantAdmin,
  });

  const filteredStatuses = itemStatuses.filter(
    (status) =>
      status.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      status.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const borrowableCount = itemStatuses.filter((s) => s.isBorrowable).length;
  const visibleCount = itemStatuses.filter((s) => s.isVisibleInOPAC).length;
  const inventoryCount = itemStatuses.filter((s) => s.affectsInventory).length;

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to view item statuses.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Item Statuses"
          description="View the system-defined statuses that control item availability"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Item Statuses" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by code or label..."
        />

        {/* Info Card */}
        <Card className="p-4 mb-6 bg-white border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-gray-50">
              <Info className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h4 className="mb-1 font-medium text-gray-900">System-Controlled Statuses</h4>
              <p className="text-sm text-gray-600">
                These statuses are built into the system and cannot be changed. They determine
                whether an item can be borrowed, appears in the catalog, and affects your inventory
                counts. Status changes happen automatically when items are checked out or returned,
                or can be set manually by staff (e.g., marking items as lost or under repair).
              </p>
            </div>
          </div>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CircleCheckBig className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{borrowableCount}</p>
                <p className="text-sm text-gray-500">Borrowable Statuses</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Eye className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{visibleCount}</p>
                <p className="text-sm text-gray-500">Visible in Catalog</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{inventoryCount}</p>
                <p className="text-sm text-gray-500">Affect Inventory</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Statuses List */}
        <Card className="overflow-hidden bg-white shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              All Statuses ({itemStatuses.length})
            </h2>
          </div>

          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading statuses..." />
            </div>
          ) : filteredStatuses.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Package className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching statuses" : "No statuses available"}
              </h3>
              <p className="max-w-sm mx-auto text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term"
                  : "Item statuses will appear here once configured."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredStatuses.map((status) => (
                <div key={status.code} className="p-5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{status.label}</h3>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono rounded">
                          {status.code}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        {/* Borrowable Badge */}
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                            status.isBorrowable
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          )}
                        >
                          {status.isBorrowable ? (
                            <CircleCheckBig className="w-3.5 h-3.5" />
                          ) : (
                            <CircleOff className="w-3.5 h-3.5" />
                          )}
                          {status.isBorrowable ? "Borrowable" : "Not Borrowable"}
                        </span>

                        {/* Visibility Badge */}
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                            status.isVisibleInOPAC
                              ? "bg-primary/10 text-primary"
                              : "bg-gray-100 text-gray-600"
                          )}
                        >
                          {status.isVisibleInOPAC ? (
                            <Eye className="w-3.5 h-3.5" />
                          ) : (
                            <EyeOff className="w-3.5 h-3.5" />
                          )}
                          {status.isVisibleInOPAC ? "Visible in Catalog" : "Hidden from Catalog"}
                        </span>

                        {/* Inventory Badge */}
                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                            status.affectsInventory
                              ? "bg-purple-100 text-purple-700"
                              : "bg-gray-100 text-gray-600"
                          )}
                        >
                          <BarChart3 className="w-3.5 h-3.5" />
                          {status.affectsInventory ? "Affects Inventory" : "No Inventory Impact"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Help Text */}
        <div className="p-4 mt-6 bg-white border border-gray-200 rounded-lg">
          <h4 className="mb-1 font-medium text-gray-900">Understanding status properties</h4>
          <ul className="space-y-1 text-sm text-gray-600 list-disc list-inside">
            <li>
              <strong>Borrowable:</strong> Items with this status can be checked out to patrons
            </li>
            <li>
              <strong>Visible in Catalog:</strong> Items appear in patron-facing search results
            </li>
            <li>
              <strong>Affects Inventory:</strong> Items count toward your library's holdings
            </li>
          </ul>
        </div>
      </PageContainer>
    </AppLayout>
  );
};

export default ItemStatusesSettingsPage;
